import {
  isTrackReference,
  isTrackReferencePinned,
} from "@livekit/components-core";
import {
  AudioTrack,
  FocusToggle,
  LockLockedIcon,
  ParticipantContextIfNeeded,
  ScreenShareIcon,
  TrackMutedIndicator,
  TrackRefContextIfNeeded,
  useEnsureTrackRef,
  useFeatureContext,
  useIsEncrypted,
  useMaybeLayoutContext,
  useParticipantContext,
  useParticipantTile,
  useRoomContext,
  VideoTrack,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import * as React from "react";
import { LiveInterviewRemoteScreenShareVideo } from "./LiveInterviewRemoteScreenShareVideo";
import {
  getParticipantAvatarColor,
  getParticipantInitials,
  resolveParticipantDisplayName,
} from "../utils/liveInterviewDisplayUtils";
import { isScreenShareTrackRef } from "../utils/liveInterviewTrackUtils";
import { useParticipantHandRaised } from "../utils/liveInterviewRaiseHand";
import { LiveInterviewHandRaisedSymbol } from "./LiveInterviewHandRaisedSymbol";

function LiveInterviewParticipantName() {
  const participant = useParticipantContext();
  const displayName = resolveParticipantDisplayName(participant);
  return <span className="lk-participant-name">{displayName}</span>;
}

function LiveInterviewParticipantHandRaisedBadge() {
  const participant = useParticipantContext();
  const handRaised = useParticipantHandRaised(participant);
  if (!handRaised) return null;

  return (
    <div className="li-participant-hand-raised" role="status" aria-label="Hand raised">
      <LiveInterviewHandRaisedSymbol size="sm" />
    </div>
  );
}

function LiveInterviewParticipantPlaceholder() {
  const participant = useParticipantContext();
  const displayName = resolveParticipantDisplayName(participant);
  const initials = getParticipantInitials(displayName);
  const avatarColor = getParticipantAvatarColor(displayName);

  return (
    <div className="lk-participant-placeholder li-participant-placeholder">
      <span
        className="li-participant-avatar-bubble"
        style={{ backgroundColor: avatarColor }}
        aria-hidden
      >
        {initials}
      </span>
    </div>
  );
}

/** Static tile for the local sharer — avoids rendering live screen capture in-meeting (hall of mirrors). */
function LiveInterviewPresentingPlaceholder() {
  return (
    <div className="li-presenting-placeholder" role="status" aria-live="polite">
      <ScreenShareIcon className="li-presenting-placeholder-icon" aria-hidden />
      <p className="li-presenting-placeholder-title">You&apos;re presenting to everyone</p>
      <p className="li-presenting-placeholder-sub">
        Your screen is being shared. Others can see your display.
      </p>
    </div>
  );
}

export const LiveInterviewParticipantTile = React.forwardRef(function LiveInterviewParticipantTile(
  {
    trackRef,
    children,
    onParticipantClick,
    disableSpeakingIndicator,
    ...htmlProps
  },
  ref
) {
  const trackReference = useEnsureTrackRef(trackRef);
  const { elementProps } = useParticipantTile({
    htmlProps,
    disableSpeakingIndicator,
    onParticipantClick,
    trackRef: trackReference,
  });
  const isEncrypted = useIsEncrypted(trackReference.participant);
  const layoutContext = useMaybeLayoutContext();
  const autoManageSubscription = useFeatureContext()?.autoSubscription;
  const room = useRoomContext();
  const roomKey = room?.name || room?.sid || "";

  const handleSubscribe = React.useCallback(
    (subscribed) => {
      if (
        trackReference.source &&
        !subscribed &&
        layoutContext &&
        layoutContext.pin.dispatch &&
        isTrackReferencePinned(trackReference, layoutContext.pin.state)
      ) {
        layoutContext.pin.dispatch({ msg: "clear_pin" });
      }
    },
    [trackReference, layoutContext]
  );

  const isLocalScreenShare =
    isScreenShareTrackRef(trackReference) && trackReference.participant.isLocal;

  const suppressScreenSharePlayback =
    isScreenShareTrackRef(trackReference) && trackReference.participant.isLocal;

  const renderVideoTrack = () => {
    if (
      isScreenShareTrackRef(trackReference) &&
      !trackReference.participant.isLocal &&
      !suppressScreenSharePlayback
    ) {
      return (
        <LiveInterviewRemoteScreenShareVideo
          trackRef={trackReference}
          onSubscriptionStatusChanged={handleSubscribe}
          manageSubscription={autoManageSubscription}
          roomKey={roomKey}
        />
      );
    }

    return (
      <VideoTrack
        trackRef={trackReference}
        onSubscriptionStatusChanged={handleSubscribe}
        manageSubscription={autoManageSubscription}
      />
    );
  };

  return (
    <div ref={ref} style={{ position: "relative" }} {...elementProps}>
      <TrackRefContextIfNeeded trackRef={trackReference}>
        <ParticipantContextIfNeeded participant={trackReference.participant}>
          {children ?? (
            <>
              {suppressScreenSharePlayback ? (
                isLocalScreenShare ? <LiveInterviewPresentingPlaceholder /> : null
              ) : isTrackReference(trackReference) &&
                (trackReference.publication?.kind === "video" ||
                  trackReference.source === Track.Source.Camera ||
                  isScreenShareTrackRef(trackReference)) ? (
                renderVideoTrack()
              ) : (
                isTrackReference(trackReference) && (
                  <AudioTrack
                    trackRef={trackReference}
                    onSubscriptionStatusChanged={handleSubscribe}
                  />
                )
              )}
              {!suppressScreenSharePlayback ? <LiveInterviewParticipantPlaceholder /> : null}
              {trackReference.source === Track.Source.Camera ? (
                <LiveInterviewParticipantHandRaisedBadge />
              ) : null}
              <div className="lk-participant-metadata">
                <div className="lk-participant-metadata-item">
                  {trackReference.source === Track.Source.Camera ? (
                    <>
                      {isEncrypted && <LockLockedIcon style={{ marginRight: "0.25rem" }} />}
                      <TrackMutedIndicator
                        trackRef={{
                          participant: trackReference.participant,
                          source: Track.Source.Microphone,
                        }}
                        show="muted"
                      />
                      <LiveInterviewParticipantName />
                    </>
                  ) : (
                    <>
                      <ScreenShareIcon style={{ marginRight: "0.25rem" }} />
                      {isLocalScreenShare ? (
                        <>You&apos;re presenting</>
                      ) : (
                        <>
                          <LiveInterviewParticipantName />
                          &apos;s screen
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          <FocusToggle trackRef={trackReference} />
        </ParticipantContextIfNeeded>
      </TrackRefContextIfNeeded>
    </div>
  );
});

export function LiveInterviewFocusLayout({ trackRef, ...htmlProps }) {
  return <LiveInterviewParticipantTile trackRef={trackRef} {...htmlProps} />;
}
